/**
 * Settings Store
 * Zustand store for user settings with localStorage persistence
 * Maintains backward compatibility with vanilla JS localStorage keys
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Settings } from '../types'
import { DEFAULT_SETTINGS } from '@config/settings'
import { STORAGE_KEYS, OPERATION_LIMITS, WORD_PRACTICE } from '@config/constants'
import {
  sanitizeFontOption,
  sanitizeMaxValue,
  sanitizeTimerSetting,
} from '@lib/validation'

export interface SettingsStore {
  settings: Settings
  updateSettings: (updates: Partial<Settings>) => void
  updatePracticeMode: (practiceMode: Settings['practiceMode']) => void
  updateMode: (mode: Settings['mode']) => void
  updateTheme: (theme: Settings['theme']) => void
  updateFontSizes: (fonts: Partial<Settings['fonts']>) => void
  updateMaxValue: (operation: keyof Settings['maxValues'], value: number) => void
  updateEnabledOperations: (updates: Partial<Settings['enabledOperations']>) => void
  updateWordPractice: (updates: Partial<Settings['wordPractice']>) => void
  updateLettersPractice: (updates: Partial<Settings['lettersPractice']>) => void
  updateCelebrations: (updates: Partial<Settings['celebrations']>) => void
  updateTimers: (updates: Partial<Settings['timers']>) => void
  resetSettings: () => void
}

export function cloneDefaultSettings(): Settings {
  return {
    practiceMode: DEFAULT_SETTINGS.practiceMode,
    mode: DEFAULT_SETTINGS.mode,
    theme: DEFAULT_SETTINGS.theme,
    fonts: { ...DEFAULT_SETTINGS.fonts },
    maxValues: { ...DEFAULT_SETTINGS.maxValues },
    enabledOperations: { ...DEFAULT_SETTINGS.enabledOperations },
    wordPractice: { ...DEFAULT_SETTINGS.wordPractice },
    lettersPractice: { ...DEFAULT_SETTINGS.lettersPractice },
    celebrations: { ...DEFAULT_SETTINGS.celebrations },
    timers: { ...DEFAULT_SETTINGS.timers },
  }
}

export function sanitizeLoadedSettings(stored: unknown): Settings {
  const defaults = cloneDefaultSettings()

  if (!stored || typeof stored !== 'object') {
    return defaults
  }

  const parsed = stored as Partial<Settings>

  // Sanitize practice mode
  let practiceMode = defaults.practiceMode
  if (parsed.practiceMode === 'word') practiceMode = 'word'
  if (parsed.practiceMode === 'letters') practiceMode = 'letters'

  // Sanitize mode
  let mode = defaults.mode
  if (parsed.mode === 'multipleChoice') {
    mode = 'multipleChoice'
  }

  // Sanitize theme
  let theme = defaults.theme
  if (parsed.theme === 'light' || parsed.theme === 'dark' || parsed.theme === 'system') {
    theme = parsed.theme
  }

  // Sanitize fonts
  const fonts = { ...defaults.fonts }
  if (parsed.fonts) {
    fonts.question = sanitizeFontOption(
      parsed.fonts.question as string,
      defaults.fonts.question
    )
    fonts.answer = sanitizeFontOption(parsed.fonts.answer as string, defaults.fonts.answer)
  }

  // Sanitize max values
  const maxValues = { ...defaults.maxValues }
  if (parsed.maxValues) {
    maxValues.multiplication = sanitizeMaxValue(
      parsed.maxValues.multiplication || defaults.maxValues.multiplication,
      defaults.maxValues.multiplication,
      OPERATION_LIMITS.MULTIPLICATION.min,
      OPERATION_LIMITS.MULTIPLICATION.max
    )
    maxValues.division = sanitizeMaxValue(
      parsed.maxValues.division || defaults.maxValues.division,
      defaults.maxValues.division,
      OPERATION_LIMITS.DIVISION.min,
      OPERATION_LIMITS.DIVISION.max
    )
    maxValues.addition = sanitizeMaxValue(
      parsed.maxValues.addition || defaults.maxValues.addition,
      defaults.maxValues.addition,
      OPERATION_LIMITS.ADDITION.min,
      OPERATION_LIMITS.ADDITION.max
    )
    maxValues.subtraction = sanitizeMaxValue(
      parsed.maxValues.subtraction || defaults.maxValues.subtraction,
      defaults.maxValues.subtraction,
      OPERATION_LIMITS.SUBTRACTION.min,
      OPERATION_LIMITS.SUBTRACTION.max
    )
  }

  // Sanitize enabled operations
  const enabledOperations = { ...defaults.enabledOperations }
  if (parsed.enabledOperations && typeof parsed.enabledOperations === 'object') {
    enabledOperations.multiplication = Boolean(parsed.enabledOperations.multiplication ?? defaults.enabledOperations.multiplication)
    enabledOperations.division = Boolean(parsed.enabledOperations.division ?? defaults.enabledOperations.division)
    enabledOperations.addition = Boolean(parsed.enabledOperations.addition ?? defaults.enabledOperations.addition)
    enabledOperations.subtraction = Boolean(parsed.enabledOperations.subtraction ?? defaults.enabledOperations.subtraction)
  }

  // Sanitize word practice
  const wordPractice = { ...defaults.wordPractice }
  if (parsed.wordPractice) {
    wordPractice.enabled = Boolean(parsed.wordPractice.enabled)
    wordPractice.difficulty = sanitizeMaxValue(
      parsed.wordPractice.difficulty || defaults.wordPractice.difficulty,
      defaults.wordPractice.difficulty,
      WORD_PRACTICE.MIN_DIFFICULTY,
      WORD_PRACTICE.MAX_DIFFICULTY
    )
  }

  // Sanitize letters practice
  const lettersPractice = { ...defaults.lettersPractice }
  if (parsed.lettersPractice) {
    lettersPractice.enabled = Boolean(parsed.lettersPractice.enabled)
    const parsedLevel = parsed.lettersPractice.level
    if (parsedLevel === 1 || parsedLevel === 2 || parsedLevel === 3) {
      lettersPractice.level = parsedLevel
    }
  }

  // Sanitize celebrations
  const celebrations = {
    ...defaults.celebrations,
    ...(parsed.celebrations && typeof parsed.celebrations === 'object'
      ? parsed.celebrations
      : {}),
  }
  celebrations.confetti = Boolean(celebrations.confetti)

  // Sanitize timers
  const timers = {
    math: sanitizeTimerSetting(parsed?.timers?.math ?? defaults.timers.math, defaults.timers.math),
    word: sanitizeTimerSetting(parsed?.timers?.word ?? defaults.timers.word, defaults.timers.word),
  }

  return {
    practiceMode,
    mode,
    theme,
    fonts,
    maxValues,
    enabledOperations,
    wordPractice,
    lettersPractice,
    celebrations,
    timers,
  }
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      settings: cloneDefaultSettings(),

      updateSettings: (updates) =>
        set((state) => ({
          settings: { ...state.settings, ...updates },
        })),

      updatePracticeMode: (practiceMode) =>
        set((state) => ({
          settings: {
            ...state.settings,
            practiceMode,
            wordPractice: {
              ...state.settings.wordPractice,
              enabled: practiceMode === 'word',
            },
            lettersPractice: {
              ...state.settings.lettersPractice,
              enabled: practiceMode === 'letters',
            },
          },
        })),

      updateMode: (mode) =>
        set((state) => ({
          settings: { ...state.settings, mode },
        })),

      updateTheme: (theme) =>
        set((state) => ({
          settings: { ...state.settings, theme },
        })),

      updateFontSizes: (fonts) =>
        set((state) => ({
          settings: {
            ...state.settings,
            fonts: { ...state.settings.fonts, ...fonts },
          },
        })),

      updateMaxValue: (operation, value) =>
        set((state) => ({
          settings: {
            ...state.settings,
            maxValues: {
              ...state.settings.maxValues,
              [operation]: value,
            },
          },
        })),

      updateEnabledOperations: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            enabledOperations: { ...state.settings.enabledOperations, ...updates },
          },
        })),

      updateWordPractice: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            wordPractice: { ...state.settings.wordPractice, ...updates },
          },
        })),

      updateLettersPractice: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            lettersPractice: { ...state.settings.lettersPractice, ...updates },
          },
        })),

      updateCelebrations: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            celebrations: { ...state.settings.celebrations, ...updates },
          },
        })),

      updateTimers: (updates) =>
        set((state) => ({
          settings: {
            ...state.settings,
            timers: { ...state.settings.timers, ...updates },
          },
        })),

      resetSettings: () =>
        set({
          settings: cloneDefaultSettings(),
        }),
    }),
    {
      name: STORAGE_KEYS.settings,
      partialize: (state) => ({ settings: state.settings }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Sanitize settings on load
          const stored = localStorage.getItem(STORAGE_KEYS.settings)
          if (stored) {
            try {
              const parsed = JSON.parse(stored)
              const sanitized = sanitizeLoadedSettings(parsed.state?.settings || parsed)
              state.settings = sanitized
            } catch (error) {
              console.warn('Failed to parse stored settings:', error)
            }
          }
        }
      },
    }
  )
)
